import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Save, X, Plus, Edit, Trash2, BookOpen, Wand2 } from 'lucide-react';
import { RichTextEditor, VideoInput, DocumentInput } from '../../ContentEditors';
import LessonPlanBuilderModal, { planToMarkdown } from '../../LessonPlanBuilderModal';

const ModuleForm = ({
 moduleForm,
 setModuleForm,
 editingModule,
 courses,
 moduleContentSectionForm,
 setModuleContentSectionForm,
 editingModuleSectionId,
 onSave,
 onCancel,
 onAddContentSection,
 onEditContentSection,
 onRemoveContentSection,
 onCancelEditSection
}) => {
 // "Build with AI" — module-level plan builder. Reuses the same modal as
 // the lesson form; on Insert we append the generated markdown to the
 // module description and fold any quiz items into a quiz content
 // section so authors don't have to retype them.
 const [showPlanBuilder, setShowPlanBuilder] = useState(false);

 const parentCourseTitle = (() => {
 if (!moduleForm.course_id) return '';
 const c = (courses || []).find((cc) => cc.id === moduleForm.course_id);
 return c?.title || '';
 })();

 const handlePlanInsert = (_md, plan) => {
 const current = moduleForm.description || '';
 const quizItems = Array.isArray(plan?.quiz) ? plan.quiz : [];
 const nextSections = [...(moduleForm.content_sections || [])];
 let addedQuizSection = false;
 if (quizItems.length > 0) {
 const questions = quizItems.map((q) => {
 const options = Array.isArray(q.options) ? q.options.slice(0, 4) : ['', '', '', ''];
 while (options.length < 4) options.push('');
 const idx = Number.isInteger(q.answer_index) ? q.answer_index : 0;
 const safeIdx = Math.max(0, Math.min(options.length - 1, idx));
 return {
 question: q.question || '',
 type: 'multiple_choice',
 options,
 correct_answer: options[safeIdx] || '',
 explanation: q.explanation || '',
 };
 }).filter((q) => q.question);
 if (questions.length > 0) {
 nextSections.push({
 id: `temp_${Date.now()}_quiz`,
 title: `AI-generated quiz — ${plan?.title || 'Module'}`,
 type: 'quiz',
 order: nextSections.length,
 video_url: '',
 text_content: '',
 markdown_content: '',
 pdf_url: '',
 presentation_url: '',
 quiz_data: { passing_score: 70, questions },
 checklist_data: { items: [], require_all: true },
 poll_data: { question: '', options: [], allow_multiple: false },
 });
 addedQuizSection = true;
 }
 }
 const md = planToMarkdown(plan, { includeQuiz: !addedQuizSection });
 const asHtml = md
 .split('\n')
 .map((line) => (line.trim()
 ? `<div>${line.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>`
 : '<div><br/></div>'))
 .join('');
 setModuleForm({
 ...moduleForm,
 description: `${current}${current ? '<div><br/></div>' : ''}${asHtml}`,
 content_sections: nextSections,
 });
 };

 return (
 <Card className="mb-6">
 <CardHeader>
 <div className="flex items-start justify-between gap-3 flex-wrap">
 <div>
 <CardTitle>{editingModule ? 'Edit Module' : 'Add Module to Course'}</CardTitle>
 <CardDescription>
 {editingModule ? 'Update module details' : 'Create a new module and assign it to a course'}
 </CardDescription>
 </div>
 <Button
 variant="outline"
 size="sm"
 onClick={() => setShowPlanBuilder(true)}
 data-testid="module-plan-builder-open-btn"
 title="Build a module outline with AI grounded in your Knowledge Base + Standards of Education"
 >
 <Wand2 className="w-4 h-4 mr-2 text-amber-500" />
 Build with AI
 </Button>
 </div>
 </CardHeader>
 <CardContent>
 <LessonPlanBuilderModal
 open={showPlanBuilder}
 onOpenChange={setShowPlanBuilder}
 onInsert={handlePlanInsert}
 initialTopic={moduleForm.title || ''}
 initialTier="pro"
 moduleTitle={parentCourseTitle}
 />
 {/* Breadcrumb showing parent course (compact) */}
 {moduleForm.course_id && (
 <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
 <div className="flex items-center gap-2 text-sm">
 <BookOpen className="w-4 h-4 text-blue-600" />
 <span className="text-slate-600 dark:text-neutral-400">Course:</span>
 <span className="font-semibold text-slate-900 dark:text-white">
 {courses.find(c => c.id === moduleForm.course_id)?.title || 'Selected Course'}
 </span>
 </div>
 </div>
 )}
 <div className="space-y-4">
 {/* Course Selection */}
 <div>
 <Label>Select Course *</Label>
 <select
 value={moduleForm.course_id}
 onChange={(e) => setModuleForm({ ...moduleForm, course_id: e.target.value })}
 className="w-full p-2 border rounded-lg"
 >
 <option value="">-- Select a course --</option>
 {courses.map(course => (
 <option key={course.id} value={course.id}>
 {course.title} ({course.tier})
 </option>
 ))}
 </select>
 </div>
 
 <div>
 <Label>Module Title</Label>
 <Input
 value={moduleForm.title}
 onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
 placeholder="Module 1: Getting Started"
 />
 </div>
 <div>
 <Label>Description (optional)</Label>
 <RichTextEditor
 value={moduleForm.description}
 onChange={(value) => setModuleForm({ ...moduleForm, description: value })}
 placeholder="Module description..."
 />
 </div>
 <div>
 <Label>Order</Label>
 <Input
 type="number"
 value={moduleForm.order}
 onChange={(e) => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) })}
 />
 </div>

 {/* Multi-Content Module Builder */}
 <div className="border-2 border-primary/30 dark:border-primary/30 rounded-lg p-4 bg-primary/5 dark:bg-navy/20">
 <h4 className="font-semibold mb-3 flex items-center gap-2 text-primary dark:text-primary">
 🎬 Multi-Content Module Overview
 </h4>
 <p className="text-sm text-slate-600 dark:text-neutral-400 mb-4">
 Add rich content to introduce this module (videos, text, PDFs, presentations)
 </p>

 {/* Existing Content Sections */}
 {moduleForm.content_sections.length > 0 && (
 <div className="mb-4 space-y-2">
 <Label className="text-sm font-semibold">Current Sections ({moduleForm.content_sections.length})</Label>
 {moduleForm.content_sections.map((section, index) => (
 <div 
 key={section.id} 
 className={`flex items-center gap-2 p-3 rounded border ${
 editingModuleSectionId === section.id 
 ? 'bg-primary/10 dark:bg-navy/30 border-primary/30' 
 : 'bg-white dark:bg-slate-800'
 }`}
 >
 <div className="flex-1">
 <span className="font-medium">{section.title}</span>
 <span className="ml-2 text-xs px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">
 {section.type}
 {section.type === 'quiz' && section.quiz_data?.questions?.length > 0 && (
 <span className="ml-1 font-semibold text-primary dark:text-primary">
 ({section.quiz_data.questions.length} questions)
 </span>
 )}
 </span>
 {editingModuleSectionId === section.id && (
 <span className="ml-2 text-xs px-2 py-1 bg-primary text-white rounded">
 ✏️ Editing
 </span>
 )}
 </div>
 <Button
 size="sm"
 variant="outline"
 onClick={() => onEditContentSection(section)}
 disabled={editingModuleSectionId !== null && editingModuleSectionId !== section.id}
 >
 <Edit className="w-4 h-4" />
 </Button>
 <Button
 size="sm"
 variant="ghost"
 onClick={() => onRemoveContentSection(section.id)}
 className="text-red-600"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 ))}
 </div>
 )}

 {/* Add New Section Form */}
 <div className="border-t pt-4 space-y-3">
 <div className="flex items-center justify-between">
 <Label className="text-sm font-semibold">
 {editingModuleSectionId ? '✏️ Edit Section' : '➕ Add New Section'}
 </Label>
 {editingModuleSectionId && (
 <Button
 size="sm"
 variant="ghost"
 onClick={onCancelEditSection}
 className="text-xs"
 >
 Cancel Edit
 </Button>
 )}
 </div>

 <div>
 <Label>Section Title</Label>
 <Input
 value={moduleContentSectionForm.title}
 onChange={(e) => setModuleContentSectionForm({ ...moduleContentSectionForm, title: e.target.value })}
 placeholder="Module Introduction"
 />
 </div>

 <div>
 <Label>Content Type</Label>
 <select
 value={moduleContentSectionForm.type}
 onChange={(e) => setModuleContentSectionForm({ ...moduleContentSectionForm, type: e.target.value })}
 className="w-full p-2 border rounded-lg"
 >
 <option value="markdown">📄 Textbook (Markdown)</option>
 <option value="text">📝 Rich Text/HTML</option>
 <option value="video">📹 Video</option>
 <option value="pdf">📑 PDF Document</option>
 <option value="presentation">📊 Presentation</option>
 </select>
 </div>

 {/* Content Type Specific Fields */}
 {moduleContentSectionForm.type === 'video' && (
 <VideoInput
 value={moduleContentSectionForm.video_url}
 onChange={(value) => setModuleContentSectionForm({ ...moduleContentSectionForm, video_url: value })}
 />
 )}

 {moduleContentSectionForm.type === 'text' && (
 <div>
 <Label className="mb-2 block">Rich Text Content</Label>
 <RichTextEditor
 value={moduleContentSectionForm.text_content}
 onChange={(value) => setModuleContentSectionForm({ ...moduleContentSectionForm, text_content: value })}
 placeholder="Start writing your module overview..."
 />
 </div>
 )}

 {moduleContentSectionForm.type === 'markdown' && (
 <div>
 <Label>Markdown Content</Label>
 <textarea
 value={moduleContentSectionForm.markdown_content}
 onChange={(e) => setModuleContentSectionForm({ ...moduleContentSectionForm, markdown_content: e.target.value })}
 placeholder="# Module Overview\n\nYour markdown content..."
 className="w-full p-2 border rounded-lg min-h-[120px] font-mono text-sm"
 />
 </div>
 )}

 {moduleContentSectionForm.type === 'pdf' && (
 <DocumentInput
 value={moduleContentSectionForm.pdf_url}
 onChange={(value) => setModuleContentSectionForm({ ...moduleContentSectionForm, pdf_url: value })}
 type="pdf"
 />
 )}

 {moduleContentSectionForm.type === 'presentation' && (
 <DocumentInput
 value={moduleContentSectionForm.presentation_url}
 onChange={(value) => setModuleContentSectionForm({ ...moduleContentSectionForm, presentation_url: value })}
 type="presentation"
 />
 )}

 <Button
 onClick={onAddContentSection}
 variant="outline"
 className={`w-full ${
 editingModuleSectionId 
 ? 'bg-green-600 hover:bg-green-700' 
 : 'bg-primary hover:bg-primary/90'
 } text-white`}
 >
 {editingModuleSectionId ? (
 <>
 <Save className="w-4 h-4 mr-2" />
 Update Section
 </>
 ) : (
 <>
 <Plus className="w-4 h-4 mr-2" />
 Add Section to Module
 </>
 )}
 </Button>
 </div>
 </div>

 {/* Support Materials Section */}
 <div className="border-t pt-4 mt-4">
 <h4 className="font-semibold mb-3 flex items-center gap-2">
 📎 Module Support Materials (Optional)
 </h4>
 <p className="text-sm text-slate-600 dark:text-neutral-400 mb-3">
 Add overview videos, reference images, and resources for this entire module
 </p>
 <div className="space-y-3">
 {/* Support Photos */}
 <div>
 <Label>Photos (one URL per line)</Label>
 <textarea
 value={moduleForm.support_photos?.join('\n') || ''}
 onChange={(e) => setModuleForm({ 
 ...moduleForm, 
 support_photos: e.target.value.split('\n').filter(url => url.trim()) 
 })}
 placeholder="https://example.com/module-overview.jpg&#10;https://example.com/reference-image.jpg"
 className="w-full p-2 border rounded-lg min-h-[80px] font-mono text-sm"
 />
 </div>

 {/* Support Videos */}
 <div>
 <Label>Videos (one YouTube URL per line)</Label>
 <textarea
 value={moduleForm.support_videos?.join('\n') || ''}
 onChange={(e) => setModuleForm({ 
 ...moduleForm, 
 support_videos: e.target.value.split('\n').filter(url => url.trim()) 
 })}
 placeholder="https://www.youtube.com/watch?v=...&#10;https://www.youtube.com/watch?v=..."
 className="w-full p-2 border rounded-lg min-h-[80px] font-mono text-sm"
 />
 </div>

 {/* Support Links */}
 <div>
 <Label>Related Links (Title | URL format, one per line)</Label>
 <textarea
 value={moduleForm.support_links?.map(link => `${link.title} | ${link.url}`).join('\n') || ''}
 onChange={(e) => {
 const links = e.target.value.split('\n')
 .filter(line => line.trim())
 .map(line => {
 const parts = line.split('|').map(p => p.trim());
 return { title: parts[0] || '', url: parts[1] || '' };
 });
 setModuleForm({ ...moduleForm, support_links: links });
 }}
 placeholder="Module Resources | https://example.com/resources&#10;Tutorial Series | https://youtube.com/..."
 className="w-full p-2 border rounded-lg min-h-[80px] font-mono text-sm"
 />
 </div>

 {/* Support Downloads */}
 <div>
 <Label>Downloadable Files (Title | URL format, one per line)</Label>
 <textarea
 value={moduleForm.support_downloads?.map(dl => `${dl.title} | ${dl.url}`).join('\n') || ''}
 onChange={(e) => {
 const downloads = e.target.value.split('\n')
 .filter(line => line.trim())
 .map(line => {
 const parts = line.split('|').map(p => p.trim());
 return { title: parts[0] || '', url: parts[1] || '' };
 });
 setModuleForm({ ...moduleForm, support_downloads: downloads });
 }}
 placeholder="Module Workbook | https://example.com/workbook.pdf&#10;Cheat Sheet | https://example.com/cheatsheet.pdf"
 className="w-full p-2 border rounded-lg min-h-[80px] font-mono text-sm"
 />
 </div>
 </div>
 </div>

 {/* Advertising Section */}
 <div className="border-t pt-4 mt-4">
 <h4 className="font-semibold mb-3 flex items-center gap-2">
 📢 Advertisement Settings
 </h4>
 <div className="space-y-3">
 <div className="flex items-center gap-2">
 <input
 type="checkbox"
 checked={moduleForm.ad_enabled || false}
 onChange={(e) => setModuleForm({ ...moduleForm, ad_enabled: e.target.checked })}
 className="w-4 h-4"
 />
 <Label>Enable Advertisement for this Module</Label>
 </div>
 
 {moduleForm.ad_enabled && (
 <>
 <div>
 <Label>Ad Title</Label>
 <Input
 value={moduleForm.ad_title || ''}
 onChange={(e) => setModuleForm({ ...moduleForm, ad_title: e.target.value })}
 placeholder="Special Offer!"
 />
 </div>
 <div>
 <Label>Ad Content</Label>
 <Input
 value={moduleForm.ad_content || ''}
 onChange={(e) => setModuleForm({ ...moduleForm, ad_content: e.target.value })}
 placeholder="Limited time discount on premium tools..."
 />
 </div>
 <div>
 <Label>Ad Image URL</Label>
 <Input
 value={moduleForm.ad_image_url || ''}
 onChange={(e) => setModuleForm({ ...moduleForm, ad_image_url: e.target.value })}
 placeholder="https://..."
 />
 </div>
 <div>
 <Label>Ad Link URL</Label>
 <Input
 value={moduleForm.ad_link_url || ''}
 onChange={(e) => setModuleForm({ ...moduleForm, ad_link_url: e.target.value })}
 placeholder="https://..."
 />
 </div>
 <div>
 <Label>Ad Link Text</Label>
 <Input
 value={moduleForm.ad_link_text || ''}
 onChange={(e) => setModuleForm({ ...moduleForm, ad_link_text: e.target.value })}
 placeholder="Get Offer"
 />
 </div>
 </>
 )}
 </div>
 </div>
 </div>
 <div className="flex gap-3 mt-4">
 <Button onClick={onSave} className="bg-green-600 hover:bg-green-700">
 <Save className="w-4 h-4 mr-2" />
 {editingModule ? 'Update Module' : 'Create Module'}
 </Button>
 <Button onClick={onCancel} variant="outline">
 <X className="w-4 h-4 mr-2" />
 Cancel
 </Button>
 </div>
 </CardContent>
 </Card>
 );
};

export default ModuleForm;
